import { isIosCompanionApp } from "@/lib/native/runtime";
import type { AppNotification } from "./hooks/useNotifications";

/**
 * What a feed entry SAYS, in the seller's language.
 *
 * The stored `title`/`body` are written by the worker in English, at the moment
 * the event happens — so an Arabic portal showed "New Order Received!" and
 * "Order #… has been placed on …". The record also carries the `type` and the
 * `data` the sentence was built from, which is enough to build it again in
 * whichever language the seller is reading.
 *
 * Anything not listed here falls back to the stored strings: an unknown type is
 * still worth showing, and this must never turn a notification blank.
 */
export function notificationCopy(
  notification: AppNotification,
  t: (key: string, values?: Record<string, string | number>) => string,
): { title: string; body: string; localized: boolean } {
  const k = (name: string) => `notifications.${name}`;
  const data = notification.data ?? {};

  switch (notification.type) {
    case "store.subscription.changed":
      if (isIosCompanionApp()) return {
        title: t("iosCompanion.accessTitle"),
        body: t("iosCompanion.subscriptionUpdated"),
        localized: true,
      };
      return { title: notification.title, body: notification.body, localized: false };
    case "order.created":
      return {
        title: t(k("feedOrderTitle")),
        body: t(k("feedOrderBody"), { code: data.orderCode ?? "" }),
        localized: true,
      };
    case "variant.low_stock":
      return {
        title: t(k("feedLowStockTitle")),
        body: t(k("feedLowStockBody"), { count: data.stockCount ?? "0" }),
        localized: true,
      };
    case "wallet.top-up.reviewed": {
      const confirmed = data.type === "wallet_top_up_confirmed";
      const values = { amount: data.amount ?? "", currency: data.currency ?? "" };
      return {
        title: t(k(confirmed ? "feedTopUpOkTitle" : "feedTopUpNoTitle")),
        body: t(k(confirmed ? "feedTopUpOkBody" : "feedTopUpNoBody"), values),
        localized: true,
      };
    }
    default:
      return {
        title: notification.title,
        body: notification.body,
        localized: false,
      };
  }
}
